export type ContactIdentityId = number | string;

export interface ContactOwnerSummary {
    readonly userId?: ContactIdentityId;
    readonly firstName?: string | null;
    readonly lastName?: string | null;
}

export interface ContactOwnershipSummary {
    readonly contactId: number;
    readonly owner?: ContactOwnerSummary | null;
}

export interface PrivateAvailabilityRestriction {
    readonly reason: string;
    readonly blockedContactCount: number;
}

export function isContactOwner(
    currentUserId: ContactIdentityId | null | undefined,
    ownerId: ContactIdentityId | null | undefined,
): boolean {
    if (currentUserId === null || currentUserId === undefined || ownerId === null || ownerId === undefined) {
        return false;
    }

    return String(currentUserId) === String(ownerId);
}

export function getPrivateAvailabilityRestriction(
    currentUserId: ContactIdentityId | null | undefined,
    contacts: readonly ContactOwnershipSummary[],
): PrivateAvailabilityRestriction | undefined {
    const blockedContacts = contacts.filter(contact => !isContactOwner(currentUserId, contact.owner?.userId));
    if (blockedContacts.length === 0) return undefined;

    const ownerName = blockedContacts.length === 1 ? getOwnerName(blockedContacts[0].owner) : undefined;
    if (ownerName) {
        return {
            reason: `Only ${ownerName}, the contact owner, can choose this option.`,
            blockedContactCount: 1,
        };
    }

    if (blockedContacts.length === 1) {
        return {
            reason: "Only the contact owner can choose this option.",
            blockedContactCount: 1,
        };
    }

    return {
        reason: `Only each contact's owner can choose this option. ${blockedContacts.length} selected contacts are owned by someone else.`,
        blockedContactCount: blockedContacts.length,
    };
}

function getOwnerName(owner: ContactOwnerSummary | null | undefined): string | undefined {
    const fullName = [owner?.firstName, owner?.lastName]
        .map(name => name?.trim())
        .filter((name): name is string => Boolean(name))
        .join(" ");

    return fullName || undefined;
}
